import React, { useState, useEffect } from 'react';
import { Box, Typography, Chip, Avatar, Divider, Alert } from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import { getUrl } from 'aws-amplify/storage';

const NewsPreview = ({ news, movie }) => {
  const [imageUrl, setImageUrl] = useState(null);

  useEffect(() => {
    const loadImage = async () => {
      if (news.featured_image) {
        try {
          if (news.featured_image.startsWith('http')) {
            setImageUrl(news.featured_image);
          } else {
            const result = await getUrl({ 
              key: news.featured_image, 
              options: { 
                accessLevel: 'guest',
                validateObjectExistence: false
              } 
            });
            setImageUrl(result.url.toString());
          }
        } catch (err) {
          console.error('Error loading image:', err);
          setImageUrl(null);
        }
      }
    };
    loadImage();
  }, [news.featured_image]);

  return (
    <Box>
      {news.featured_image && (
        <Box mb={3}>
          {imageUrl ? (
            <img
              src={imageUrl}
              alt={news.title}
              style={{
                width: '100%',
                maxHeight: 300,
                objectFit: 'cover',
                borderRadius: 8
              }}
              onError={(e) => {
                e.target.style.display = 'none';
              }}
            />
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>
              Image preview unavailable in admin panel. Image will display correctly in published article.
            </Alert>
          )}
        </Box>
      )}

      <Box mb={2}>
        <Typography variant="h4" component="h1" gutterBottom>
          {news.title}
        </Typography>
        
        <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
          {news.is_breaking && (
            <Chip label="Breaking" color="error" size="small" />
          )}
          {news.is_featured && (
            <Chip label="Featured" color="primary" size="small" />
          )}
          <Chip label={news.category || 'movies'} size="small" variant="outlined" />
          {news.tags?.map((tag, index) => (
            <Chip key={index} label={tag} size="small" variant="outlined" />
          ))}
        </Box>

        <Box display="flex" alignItems="center" gap={2} mb={2}>
          <Typography variant="body2" color="text.secondary">
            By {news.author || 'HypeCircle'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {news.source && `• ${news.source}`}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            • {formatDistanceToNow(new Date(news.published_at || Date.now()), { addSuffix: true })}
          </Typography>
        </Box>
      </Box>

      {movie && (
        <Box mb={3} p={2} bgcolor="grey.50" borderRadius={1}>
          <Typography variant="subtitle2" gutterBottom>
            Related Movie
          </Typography>
          <Box display="flex" gap={2} alignItems="center">
            <Avatar src={movie.poster} variant="rounded" sx={{ width: 60, height: 90 }} />
            <Box>
              <Typography variant="subtitle1">{movie.name}</Typography>
              <Typography variant="body2" color="text.secondary">
                {movie.release_date ? new Date(movie.release_date).getFullYear() : 'N/A'} • {movie.language || 'N/A'}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}

      {news.description && (
        <Box mb={3}>
          <Typography variant="h6" gutterBottom>
            Summary
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {news.description}
          </Typography>
        </Box>
      )}

      <Divider sx={{ my: 3 }} />

      <Box>
        <Typography variant="h6" gutterBottom>
          Article Content
        </Typography>
        <Box
          dangerouslySetInnerHTML={{ __html: news.content }}
          sx={{
            '& img': { maxWidth: '100%', height: 'auto' },
            '& a': { color: 'primary.main' },
            '& pre': { 
              bgcolor: 'grey.100', 
              p: 2, 
              borderRadius: 1, 
              overflow: 'auto',
              fontFamily: 'monospace'
            }
          }}
        />
      </Box>
    </Box>
  );
};

export default NewsPreview;